import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { detectInitialLocale } from "@/lib/i18n";

const DE_URL = "https://omselected.art/magazin/wie-viel-kostet-ein-fotograf";
const EN_URL = "https://omselected.art/magazin/how-much-does-a-photographer-cost";

type Section = { heading: string; body: string };
type Faq = { q: string; a: string };

const content = {
  de: {
    home: "Start",
    magazine: "Magazin",
    eyebrow: "Magazin, Preise",
    label: "Wie viel kostet ein Fotograf?",
    title: "Wie viel kostet ein Fotograf? Preise & Faktoren",
    lede: "Fotografie-Preise sind nicht willkürlich. Sie ergeben sich aus Aufwand, Erfahrung, Nutzung und dem Wert, den gute Bilder langfristig schaffen.",
    intro:
      "Wer zum ersten Mal einen Fotografen oder eine Fotografin bucht, stellt sich schnell die Frage: Was kostet das eigentlich? Die Antwort ist nicht einfach, denn der Preis hängt vom Genre, der Region, der Erfahrung und vielen weiteren Faktoren ab. Dieser Guide gibt dir einen realistischen Überblick über typische Preisbereiche und zeigt, worauf du bei Angeboten achten solltest.",
    sections: [
      {
        heading: "Was kostet ein Fotograf? Typische Preisbereiche",
        body: "Portrait-Shootings starten oft bei 200 bis 500 Euro für ein kurzes Einzelshooting. Hochzeitsfotograf:innen liegen meist zwischen 1.500 und 4.000 Euro, je nach Dauer und Paket. Produkt- und Food-Fotografie wird häufig pro Bild oder pro Halbtag abgerechnet, Fashion- und Editorial-Produktionen erreichen schnell mehrere Tausend Euro, weil Team, Location und Nutzungsrechte hinzukommen. Diese Zahlen sind Richtwerte, die je nach Region und Erfahrung deutlich variieren können.",
      },
      {
        heading: "Erfahrung und Spezialisierung",
        body: "Ein:e erfahrene:r Fotograf:in mit Jahren an Shootings und einem starken Portfolio kann mehr verlangen als ein:e Einsteiger:in. Das ist oft gerechtfertigt: Erfahrung bedeutet schnellere Arbeit, bessere Problemlösung, verlässlichere Ergebnisse und ein klarerer kreativer Blick. Spezialisierte Genres wie Hochzeit, Food oder Fashion haben außerdem eigene Abläufe, die den Preis beeinflussen.",
      },
      {
        heading: "Dauer und Umfang des Shootings",
        body: "Ein halbstündiges Headshot kostet weniger als ein ganztägiges Shooting. Dazu kommen Vorbereitung, Anfahrt, Aufbau und die Zeit für die Bildbearbeitung. Viele Fotograf:innen geben nicht die reine Shooting-Zeit an, sondern ein Paket, das alles enthält: Beratung, Aufnahmen, Auswahl und Bearbeitung.",
      },
      {
        heading: "Bildbearbeitung und Anzahl der Bilder",
        body: "Die Bearbeitung ist ein großer Teil der Leistung. Pro Bild können Auswahl, Farbkorrektur und Retusche schnell mehrere Minuten bis Stunden dauern. Achte darauf, wie viele bearbeitete Bilder im Preis enthalten sind und ob du alle unbearbeiteten Aufnahmen erhältst oder nur eine Auswahl.",
      },
      {
        heading: "Nutzungsrechte und Lizenz",
        body: "Der Preis hängt auch davon ab, wie die Bilder genutzt werden. Private Nutzung für Social Media kostet weniger als eine kommerzielle Lizenz für Werbung, Webseiten oder Print. Werde bei der Anfrage konkret: Wo sollen die Bilder erscheinen, in welcher Größe und für wie lange? So kann die Fotograf:in ein passendes Angebot machen.",
      },
      {
        heading: "Location, Reisekosten und Team",
        body: "Shootings außerhalb des Studios, Reisen zu entfernten Locations und zusätzliches Team wie Visagist:in, Stylist:in oder Assistent:in erhöhen den Preis. Das ist kein Luxus, sondern manchmal nötig, um das gewünschte Ergebnis zu erreichen. Frage früh nach, was im Angebot enthalten ist und was zusätzlich kostet.",
      },
      {
        heading: "Wie du Angebote richtig vergleichst",
        body: "Vergleiche nicht nur den Endpreis, sondern die Leistung dahinter: Wie viele Stunden sind geplant, wie viele bearbeitete Bilder erhältst du, was ist mit Nutzungsrechten, Bearbeitung, Reise und Team? Ein günstiges Angebot kann am Ende teurer werden, wenn wichtige Leistungen fehlen oder die Qualität nicht überzeugt.",
      },
      {
        heading: "Wann ist ein Angebot verdächtig günstig?",
        body: "Sehr niedrige Preise können ein Zeichen für fehlende Erfahrung, unklare Abläufe oder versteckte Kosten sein. Seriöse Fotograf:innen kommunizieren Preise transparent und bieten einen klaren Vertrag oder eine schriftliche Absprache. Wenn du das Gefühl hast, dass etwas zu gut klingt, lohnt es sich, Referenzen und vollständige Galerien anzusehen.",
      },
    ] as Section[],
    pullQuote: "Der Preis einer Fotografin ist nicht nur Zeit vor der Kamera, sondern der Wert eines Bildes, das bleibt.",
    checklistHeading: "Bevor du ein Angebot vergleichst",
    checklist: [
      "Shooting-Dauer und Anzahl der bearbeiteten Bilder klären",
      "Nutzungsrechte und Laufzeit der Lizenz absprechen",
      "Reisekosten, Location und Team-Bedarf einplanen",
      "Bearbeitung, Auswahl und Lieferzeit im Angebot prüfen",
      "Referenzen und vollständige Galerien ansehen",
      "Schriftliche Absprache oder Vertrag vereinbaren",
    ],
    faqHeading: "Häufige Fragen",
    faq: [
      {
        q: "Wie viel kostet ein Fotograf für ein Portrait-Shooting?",
        a: "Ein professionelles Portrait-Shooting liegt in Deutschland meist zwischen 200 und 500 Euro für ein kurzes Einzelshooting. Umfang, Bearbeitung und Nutzung können den Preis deutlich verändern.",
      },
      {
        q: "Was kostet eine Hochzeitsfotografin?",
        a: "Hochzeitsfotografie kostet in der Regel zwischen 1.500 und 4.000 Euro, je nach Dauer, Paket und Erfahrung. Sehr günstige Angebote solltest du kritisch prüfen.",
      },
      {
        q: "Warum gibt es so große Preisunterschiede?",
        a: "Weil der Preis von Erfahrung, Dauer, Umfang, Bildbearbeitung, Nutzungsrechten, Location und Team abhängt. Ein vollständiges Paket kostet mehr als nur eine Stunde Shooting.",
      },
      {
        q: "Was ist in einem Fotografie-Paket enthalten?",
        a: "Typischerweise enthalten sind Vorbesprechung, Shooting, Auswahl, Bildbearbeitung und eine vereinbarte Anzahl an fertigen Bildern. Reisekosten, Team und erweiterte Nutzungsrechte sind oft Zusatzleistungen.",
      },
      {
        q: "Soll ich den Preis verhandeln?",
        a: "Ein fairer Dialog ist okay, aber massives Verhandeln kann die Qualität oder Motivation beeinträchtigen. Besser: Kläre dein Budget und deine Bedürfnisse und frage nach einem passenden Paket.",
      },
    ] as Faq[],
    ctaHeading: "Preise transparent vergleichen",
    ctaBody:
      "Auf OM Selected findest du kuratierte Fotograf:innen für jedes Budget. Beschreibe dein Vorhaben in einem kurzen Briefing und erhalte passende, transparente Angebote.",
    sendRequest: "Anfrage senden",
  },
  en: {
    home: "Home",
    magazine: "Magazine",
    eyebrow: "Magazine, Pricing",
    label: "How much does a photographer cost?",
    title: "How much does a photographer cost? Prices & factors",
    lede: "Photography prices aren't arbitrary. They reflect effort, experience, usage and the long-term value that great images create.",
    intro:
      "When booking a photographer for the first time, the obvious question is: what does it actually cost? The answer isn't simple, because the price depends on genre, region, experience and many other factors. This guide gives you a realistic overview of typical price ranges and shows what to look for in quotes.",
    sections: [
      {
        heading: "What does a photographer cost? Typical price ranges",
        body: "Portrait shoots often start at €200 to €500 for a short individual session. Wedding photographers usually range from €1,500 to €4,000, depending on duration and package. Product and food photography is often billed per image or per half-day, while fashion and editorial productions can quickly reach several thousand euros, because team, location and usage rights add up. These figures are guidelines and can vary significantly by region and experience.",
      },
      {
        heading: "Experience and specialisation",
        body: "An experienced photographer with years of shoots and a strong portfolio can charge more than a beginner. This is often justified: experience means faster work, better problem solving, more reliable results and a clearer creative eye. Specialised genres like wedding, food or fashion also have their own workflows that influence the price.",
      },
      {
        heading: "Duration and scope of the shoot",
        body: "A half-hour headshot costs less than a full-day shoot. Add preparation, travel, setup and image editing time. Many photographers don't quote pure shooting time but a package that includes everything: consultation, capture, selection and retouching.",
      },
      {
        heading: "Image editing and number of images",
        body: "Post-production is a large part of the work. Per image, selection, colour correction and retouching can take several minutes to hours. Check how many edited images are included and whether you receive all unedited shots or only a curated selection.",
      },
      {
        heading: "Usage rights and licence",
        body: "The price also depends on how the images will be used. Private use for social media costs less than a commercial licence for advertising, websites or print. Be specific in your enquiry: where will the images appear, at what size and for how long? That lets the photographer tailor a quote.",
      },
      {
        heading: "Location, travel and team",
        body: "Shoots outside the studio, travel to distant locations and additional team such as make-up artist, stylist or assistant increase the price. This isn't luxury but sometimes necessary to achieve the desired result. Ask early what is included and what costs extra.",
      },
      {
        heading: "How to compare quotes properly",
        body: "Don't compare the final price alone, but the service behind it: how many hours are planned, how many edited images do you receive, what about usage rights, editing, travel and team? A cheap quote can end up more expensive if important services are missing or the quality doesn't convince.",
      },
      {
        heading: "When is a quote suspiciously cheap?",
        body: "Very low prices can signal lack of experience, unclear processes or hidden costs. Reputable photographers communicate prices transparently and offer a clear contract or written agreement. If something sounds too good to be true, it's worth checking references and full galleries.",
      },
    ] as Section[],
    pullQuote: "A photographer's price isn't just time in front of the camera, it's the value of an image that lasts.",
    checklistHeading: "Before comparing quotes",
    checklist: [
      "Clarify shoot duration and number of edited images",
      "Agree usage rights and licence duration",
      "Account for travel, location and team needs",
      "Check editing, selection and delivery time in the quote",
      "View references and complete galleries",
      "Arrange a written agreement or contract",
    ],
    faqHeading: "Frequently asked questions",
    faq: [
      {
        q: "How much does a portrait photographer cost?",
        a: "A professional portrait shoot in Germany usually ranges from €200 to €500 for a short individual session. Scope, editing and usage can change the price significantly.",
      },
      {
        q: "What does a wedding photographer cost?",
        a: "Wedding photography typically costs between €1,500 and €4,000, depending on duration, package and experience. Very cheap offers should be checked carefully.",
      },
      {
        q: "Why are prices so different?",
        a: "Because the price depends on experience, duration, scope, editing, usage rights, location and team. A full package costs more than just an hour of shooting.",
      },
      {
        q: "What is included in a photography package?",
        a: "Typically included are pre-consultation, shoot, selection, image editing and an agreed number of final images. Travel, team and extended usage rights are often extras.",
      },
      {
        q: "Should I negotiate the price?",
        a: "A fair conversation is fine, but heavy negotiation can affect quality or motivation. Better: clarify your budget and needs and ask for a suitable package.",
      },
    ] as Faq[],
    ctaHeading: "Compare prices transparently",
    ctaBody:
      "On OM Selected you'll find curated photographers for every budget. Describe your project in a short briefing and receive matching, transparent quotes.",
    sendRequest: "Send a request",
  },
};

export const Route = createFileRoute("/magazin/wie-viel-kostet-ein-fotograf")({
  head: () => {
    const isEn = detectInitialLocale() === "en";
    const c = isEn ? content.en : content.de;
    const url = isEn ? EN_URL : DE_URL;
    const description = isEn
      ? "Photographer prices explained: what portrait, wedding, product and fashion shoots cost, what factors drive the price and how to compare quotes fairly."
      : "Fotograf-Preise erklärt: Was Portrait, Hochzeit, Produkt und Fashion kosten, welche Faktoren den Preis beeinflussen und wie du Angebote fair vergleichst.";
    const faq = isEn ? content.en.faq : content.de.faq;
    return {
      meta: [
        { title: `${c.title}, OM Selected` },
        { name: "description", content: description },
        { property: "og:title", content: `${c.title}, OM Selected` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "de", href: DE_URL },
        { rel: "alternate", hrefLang: "en", href: EN_URL },
        { rel: "alternate", hrefLang: "x-default", href: DE_URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: c.title,
            description,
            url,
            inLanguage: isEn ? "en" : "de",
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://omselected.art/" },
              { "@type": "ListItem", position: 2, name: "Magazin", item: "https://omselected.art/magazin/" },
              { "@type": "ListItem", position: 3, name: c.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: PhotographerPricingPage,
});

function PhotographerPricingPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const c = isEn ? content.en : content.de;

  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] pt-12 md:pt-20 pb-16 md:pb-24">
          <nav className="eyebrow text-muted-foreground mb-6 flex gap-2">
            <Link to="/" className="hover:underline">{c.home}</Link>
            <span>/</span>
            <Link to="/magazin" className="hover:underline">{c.magazine}</Link>
          </nav>
          <span className="eyebrow text-muted-foreground block mb-6">{c.eyebrow}</span>
          <h1 className="serif text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            {c.title}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl leading-snug text-muted-foreground">
            {c.lede}
          </p>
        </div>
      </section>

      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] py-16 md:py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-muted-foreground leading-relaxed max-w-md">{c.intro}</p>
          </div>
          <div className="md:col-span-8 space-y-10">
            {c.sections.map((s) => (
              <article key={s.heading}>
                <h2 className="serif text-xl md:text-2xl leading-snug mb-3">{s.heading}</h2>
                <p className="text-muted-foreground leading-relaxed max-w-2xl">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 border-b border-border bg-[oklch(0.16_0_0)] text-background">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <blockquote className="serif italic text-2xl md:text-4xl leading-tight">
            “{c.pullQuote}”
          </blockquote>
        </div>
      </section>

      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-2xl md:text-3xl mb-8">{c.checklistHeading}</h2>
          <ul className="space-y-4">
            {c.checklist.map((item, i) => (
              <li key={item} className="flex gap-4 items-baseline">
                <span className="serif text-muted-foreground text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base md:text-lg leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-3xl md:text-4xl mb-10">{c.faqHeading}</h2>
          <div className="divide-y divide-border border-t border-border">
            {c.faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="serif text-lg md:text-xl cursor-pointer list-none flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] py-16 md:py-24 text-center">
          <h2 className="serif text-3xl md:text-4xl leading-tight">{c.ctaHeading}</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">{c.ctaBody}</p>
          <Link
            to="/brief"
            className="eyebrow inline-block mt-8 border border-foreground px-8 py-4 hover:bg-foreground hover:text-background transition-colors"
          >
            {c.sendRequest}
          </Link>
        </div>
      </section>
    </div>
  );
}
